import { useEffect, useMemo, useState } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listOrganizations } from "@/api/organizations";
import {
  type TimesheetLine,
  exportMyCsv,
  formatMinutes,
  getMySheet,
  saveMyShiftSummary,
  saveMySummary,
  submitMyPeriod,
} from "@/api/timesheets";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/ToastProvider";
import { realizedStatusColor, timesheetStatusColor } from "@/lib/timesheetStatus";

/**
 * "Ce que j'ai fait" — the practitioner's own réalisé, week by week and month
 * by month, plus the monthly summary they write and submit for validation.
 */
export function MyActivityPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [orgId, setOrgId] = useState("");
  const [granularity, setGranularity] = useState<"month" | "week">("month");
  const [summary, setSummary] = useState("");
  const [summaryDirty, setSummaryDirty] = useState(false);
  const [shiftNotes, setShiftNotes] = useState<Record<string, string>>({});

  const orgs = useQuery({
    queryKey: ["organizations-for-activity"],
    queryFn: () => listOrganizations({ limit: 100 }),
  });
  useEffect(() => {
    if (!orgId && orgs.data?.items?.length) setOrgId(orgs.data.items[0]!.id);
  }, [orgs.data, orgId]);

  const sheet = useQuery({
    queryKey: ["timesheets", "me", orgId, year, month],
    queryFn: () => getMySheet(orgId, year, month),
    enabled: Boolean(orgId),
  });

  // The server value wins whenever the month changes; local edits only survive
  // until they are saved or the user navigates away.
  useEffect(() => {
    if (!sheet.data) return;
    setSummary(sheet.data.period.summary ?? "");
    setSummaryDirty(false);
    setShiftNotes(
      Object.fromEntries(
        sheet.data.lines.map((l) => [l.assignment_id, l.practitioner_summary ?? ""]),
      ),
    );
  }, [sheet.data]);

  const period = sheet.data?.period;
  const totals = period?.totals;
  const editable = period?.status === "OPEN" || period?.status === "REJECTED";

  const saveSummaryMutation = useMutation({
    mutationFn: () => saveMySummary(orgId, year, month, summary || null),
    onSuccess: () => {
      setSummaryDirty(false);
      qc.invalidateQueries({ queryKey: ["timesheets", "me"] });
      toast(t("timesheets.summarySaved"), "success");
    },
  });

  const shiftNoteMutation = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      saveMyShiftSummary(id, text || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheets", "me"] });
      toast(t("timesheets.shiftSummarySaved"), "success");
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => submitMyPeriod(orgId, year, month, summary || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheets", "me"] });
      toast(t("timesheets.submitted"), "success");
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data
        ?.detail;
      toast(
        typeof detail === "string"
          ? t(`timesheets.error.${detail}`, { defaultValue: detail })
          : String(e),
        "error",
      );
    },
  });

  const linesByWeek = useMemo(() => {
    const map = new Map<string, TimesheetLine[]>();
    for (const line of sheet.data?.lines ?? []) {
      const d = new Date(`${line.date}T00:00:00`);
      const offset = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - offset);
      const key = d.toISOString().slice(0, 10);
      map.set(key, [...(map.get(key) ?? []), line]);
    }
    return map;
  }, [sheet.data]);

  const shiftMonth = (delta: number) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const monthTitle = new Date(year, month - 1, 1).toLocaleString(i18n.language, {
    month: "long",
    year: "numeric",
  });

  const renderLines = (lines: TimesheetLine[]) => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t("timesheets.col.date")}</TableCell>
          <TableCell>{t("timesheets.col.slot")}</TableCell>
          <TableCell>{t("timesheets.col.mode")}</TableCell>
          <TableCell>{t("timesheets.col.status")}</TableCell>
          <TableCell align="right">{t("timesheets.col.duration")}</TableCell>
          <TableCell sx={{ minWidth: 260 }}>{t("timesheets.col.mySummary")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lines.map((line) => (
          <TableRow key={line.assignment_id} hover>
            <TableCell>{line.date}</TableCell>
            <TableCell>
              {line.start_time}–{line.end_time}
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {line.slot_label}
              </Typography>
            </TableCell>
            <TableCell>
              <Chip size="small" label={line.exercise_mode_code} variant="outlined" />
              {line.functional_role_code && (
                <Chip
                  size="small"
                  label={line.functional_role_code}
                  sx={{ ml: 0.5, fontFamily: "monospace" }}
                />
              )}
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  size="small"
                  color={realizedStatusColor(line.realized_status)}
                  label={t(`planning.realized.${line.realized_status}`)}
                />
                {line.deviations.length > 0 && (
                  <Tooltip
                    title={line.deviations
                      .map((d) => t(`timesheets.deviation.${d}`, { defaultValue: d }))
                      .join(", ")}
                  >
                    <Chip size="small" variant="outlined" label={`≠${line.deviations.length}`} />
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
            <TableCell align="right">{formatMinutes(line.minutes)}</TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                variant="standard"
                placeholder={t("timesheets.shiftSummaryPlaceholder")}
                value={shiftNotes[line.assignment_id] ?? ""}
                disabled={!editable}
                onChange={(e) =>
                  setShiftNotes((prev) => ({
                    ...prev,
                    [line.assignment_id]: e.target.value,
                  }))
                }
                onBlur={(e) => {
                  if ((line.practitioner_summary ?? "") === e.target.value) return;
                  shiftNoteMutation.mutate({
                    id: line.assignment_id,
                    text: e.target.value,
                  });
                }}
              />
              {line.manager_note && (
                <Typography variant="caption" color="warning.main">
                  {t("timesheets.managerNote")}: {line.manager_note}
                </Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Box>
      <PageHeader
        title={t("myActivity.title")}
        subtitle={t("myActivity.subtitle")}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={!orgId}
              onClick={() => exportMyCsv(orgId, year, month)}
            >
              {t("timesheets.exportCsv")}
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={!editable || submitMutation.isPending}
              onClick={() => submitMutation.mutate()}
            >
              {t("timesheets.submit")}
            </Button>
          </Stack>
        }
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 2 }}
      >
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>{t("planning.organization")}</InputLabel>
          <Select
            label={t("planning.organization")}
            value={orgId}
            onChange={(e) => setOrgId(String(e.target.value))}
          >
            {(orgs.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton onClick={() => shiftMonth(-1)} aria-label="previous month">
            <ChevronLeftIcon />
          </IconButton>
          <Typography sx={{ minWidth: 180, textAlign: "center", fontWeight: 600 }}>
            {monthTitle}
          </Typography>
          <IconButton onClick={() => shiftMonth(1)} aria-label="next month">
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={granularity}
          onChange={(_, v) => v && setGranularity(v)}
        >
          <ToggleButton value="month">{t("myActivity.byMonth")}</ToggleButton>
          <ToggleButton value="week">{t("myActivity.byWeek")}</ToggleButton>
        </ToggleButtonGroup>
        {period && (
          <Chip
            color={timesheetStatusColor(period.status)}
            label={t(`timesheets.status.${period.status}`)}
          />
        )}
      </Stack>

      {period?.status === "REJECTED" && period.review_note && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("timesheets.rejectedWithReason", { reason: period.review_note })}
        </Alert>
      )}
      {period?.status === "VALIDATED" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t("timesheets.validatedOn", {
            date: period.validated_at
              ? new Date(period.validated_at).toLocaleDateString(i18n.language)
              : "",
          })}
        </Alert>
      )}

      {sheet.isLoading ? (
        <CircularProgress />
      ) : (
        <Stack spacing={2}>
          {totals && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <Stat
                  label={t("timesheets.totalHours")}
                  value={formatMinutes(totals.total_minutes)}
                />
                <Stat
                  label={t("timesheets.weightedHours")}
                  value={formatMinutes(totals.weighted_minutes)}
                />
                <Stat label={t("timesheets.realizedShifts")} value={totals.realized_shifts} />
                <Stat
                  label={t("timesheets.stillPlanned")}
                  value={totals.planned_shifts}
                  warn={totals.planned_shifts > 0}
                />
                <Stat label={t("timesheets.absentShifts")} value={totals.absent_shifts} />
                <Stat
                  label={t("timesheets.nightHours")}
                  value={formatMinutes(totals.night_minutes)}
                />
              </Stack>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {t("timesheets.monthlySummary")}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder={t("timesheets.monthlySummaryPlaceholder")}
              value={summary}
              disabled={!editable}
              onChange={(e) => {
                setSummary(e.target.value);
                setSummaryDirty(true);
              }}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button
                size="small"
                disabled={!editable || !summaryDirty || saveSummaryMutation.isPending}
                onClick={() => saveSummaryMutation.mutate()}
              >
                {t("common.save")}
              </Button>
            </Stack>
          </Paper>

          {granularity === "month" ? (
            <Paper variant="outlined">
              {(sheet.data?.lines ?? []).length === 0 ? (
                <Alert severity="info">{t("timesheets.noLines")}</Alert>
              ) : (
                renderLines(sheet.data?.lines ?? [])
              )}
            </Paper>
          ) : (
            <Stack spacing={2}>
              {(sheet.data?.weeks ?? []).map((w) => (
                <Paper key={`${w.iso_year}-${w.iso_week}`} variant="outlined">
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ p: 1.5, bgcolor: "action.hover" }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {t("myActivity.weekLabel", { week: w.iso_week })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {w.week_start}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Chip size="small" label={`${w.shifts} ${t("timesheets.shifts")}`} />
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={formatMinutes(w.minutes)}
                    />
                    {w.planned_shifts > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        label={t("timesheets.stillPlannedShort", {
                          count: w.planned_shifts,
                        })}
                      />
                    )}
                  </Stack>
                  <Divider />
                  {renderLines(linesByWeek.get(w.week_start) ?? [])}
                </Paper>
              ))}
              {(sheet.data?.weeks ?? []).length === 0 && (
                <Alert severity="info">{t("timesheets.noLines")}</Alert>
              )}
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}

function Stat({
  label,
  value,
  warn = false,
}: {
  label: string;
  value: string | number;
  warn?: boolean;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: warn ? "warning.main" : "text.primary" }}
      >
        {value}
      </Typography>
    </Box>
  );
}
